/**
 * IndexedDB Storage for recorded videos
 * Provides backup when upload fails
 */

const DB_NAME = 'RecordedVideosDB';
const DB_VERSION = 1;
const STORE_NAME = 'videos';

export const LOCAL_VIDEOS_STORAGE_CHANGED =
  'antoree-local-videos-storage-changed';

function emitLocalVideosStorageChanged(): void {
  if (typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent(LOCAL_VIDEOS_STORAGE_CHANGED));
}

export interface StoredVideo {
  id: string;
  blob: Blob;
  sessionId: string;
  courseId?: string;
  sessionTitle: string;
  timestamp: number;
  uploadStatus: 'pending' | 'uploading' | 'success' | 'failed';
  uploadAttempts: number;
  lastError?: string;
}

class VideoStorage {
  private db: IDBDatabase | null = null;

  async init(): Promise<void> {
    return new Promise((resolve, reject) => {
      const request = indexedDB.open(DB_NAME, DB_VERSION);

      request.onerror = () => reject(request.error);
      request.onsuccess = () => {
        this.db = request.result;
        resolve();
      };

      request.onupgradeneeded = event => {
        const db = (event.target as IDBOpenDBRequest).result;

        if (!db.objectStoreNames.contains(STORE_NAME)) {
          const objectStore = db.createObjectStore(STORE_NAME, {
            keyPath: 'id',
          });
          objectStore.createIndex('timestamp', 'timestamp', { unique: false });
          objectStore.createIndex('sessionId', 'sessionId', { unique: false });
          objectStore.createIndex('uploadStatus', 'uploadStatus', {
            unique: false,
          });
        }
      };
    });
  }

  async saveVideo(video: StoredVideo): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.put(video);

      request.onsuccess = () => {
        emitLocalVideosStorageChanged();
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getVideo(id: string): Promise<StoredVideo | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.get(id);

      request.onsuccess = () => resolve(request.result || null);
      request.onerror = () => reject(request.error);
    });
  }

  async getAllVideos(): Promise<StoredVideo[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.getAll();

      request.onsuccess = () => resolve(request.result || []);
      request.onerror = () => reject(request.error);
    });
  }

  async getPendingVideos(): Promise<StoredVideo[]> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('uploadStatus');
      const request = index.getAll('pending');

      request.onsuccess = () => {
        const pending = request.result || [];
        // Also get failed ones
        const failedRequest = index.getAll('failed');
        failedRequest.onsuccess = () => {
          resolve([...pending, ...(failedRequest.result || [])]);
        };
      };
      request.onerror = () => reject(request.error);
    });
  }

  async getVideoBySession(sessionId: string): Promise<StoredVideo | null> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readonly');
      const store = transaction.objectStore(STORE_NAME);
      const index = store.index('sessionId');
      // GetAll videos for this session
      const request = index.getAll(sessionId);

      request.onsuccess = () => {
        const videos = request.result || [];
        if (videos.length === 0) {
          resolve(null);
          return;
        }
        // Sort by timestamp descending (newest first)
        videos.sort((a, b) => b.timestamp - a.timestamp);
        resolve(videos[0]);
      };
      request.onerror = () => reject(request.error);
    });
  }

  async updateVideoStatus(
    id: string,
    status: StoredVideo['uploadStatus'],
    error?: string,
  ): Promise<void> {
    if (!this.db) await this.init();

    const video = await this.getVideo(id);
    if (!video) return;

    video.uploadStatus = status;
    if (error) video.lastError = error;
    if (status === 'failed') video.uploadAttempts += 1;

    await this.saveVideo(video);
  }

  async deleteVideo(id: string): Promise<void> {
    if (!this.db) await this.init();

    return new Promise((resolve, reject) => {
      const transaction = this.db!.transaction([STORE_NAME], 'readwrite');
      const store = transaction.objectStore(STORE_NAME);
      const request = store.delete(id);

      request.onsuccess = () => {
        emitLocalVideosStorageChanged();
        resolve();
      };
      request.onerror = () => reject(request.error);
    });
  }
}

export const videoStorage = new VideoStorage();
